import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Accordion, AccordionDetails, AccordionSummary, Stack, Typography} from 'views/components';
import {ExpandMore as ExpandMoreIcon} from 'views/icons';
import {Translate, translate, Helmet, useQuery} from 'views/module';

import backend from 'utils/backend';
import simplifyQuestions from 'utils/user_bio/simplify_questions';

import HeaderSection from './sections/HeaderSection';
import CustomDescriptionSection from './sections/CustomDescriptionSection';
import RecommendationChipsSection from './sections/RecommendationChipsSection';
import AvailabilitySection from './sections/AvailabilitySection';
import CredentialsSection from './sections/CredentialsSection';
import ReviewSection from './sections/ReviewSection';
import WorkHistorySection from './sections/WorkHistorySection';
import CaregiverFeedbackSection from './sections/caregiver_feedback/CaregiverFeedbackSection';
import CaregiverProfileSkeleton from './components/LoadingSkeleton';
import ProfileNotAvailable from './components/ProfileNotAvailable';
import CareApproachSection from './sections/CareApproachSection';


const CaregiverDetail = (props) => {
  const {
    caregiverRnOrShortId,
    clientData,
    statusProgressWrap: progressWrap,
  } = props;

  const query = useQuery();
  const clientRn = props.clientRn ?? query?.['client'];
  const status = query?.['status'];
  const isReviewStatus = status === 'REVIEW';
  const isStatusReview = isReviewStatus && props.clientRn;

  const [caregiverData, setCaregiverData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [expandedSectionsMap, setExpandedSectionsMap] = React.useState({});

  const questionsBio = caregiverData?.caregiverBio?.questions || {};
  const questions = simplifyQuestions(questionsBio);
  const reviews = caregiverData?.caregiverReviews?.list || [];

  const customDescription = caregiverData?.caregiverRecommendationData?.recommendation?.blob;
  const recommendationChips = caregiverData?.caregiverRecommendationData?.recommendationChips;

  const recommendationLanguageChips = caregiverData?.caregiverRecommendationData?.languageChips;
  const bioLanguageChips = (Array.isArray(questions?.languages) ? questions.languages : []).reduce((acc, languageData) => {
    const language = languageData?.name;
    if (!language) {
      return acc;
    }

    acc.push({
      language,
      callReferenceId: null,
    });
    return acc;
  }, []);
  const languageChips = isReviewStatus ? recommendationLanguageChips : bioLanguageChips;

  const availabilityData = {
    availability: caregiverData?.caregiverRecommendationData?.availability,
    availabilityLastUpdatedTime: caregiverData?.caregiverRecommendationData?.availabilityLastUpdatedTime,
  };

  React.useEffect(() => {
    progressWrap(async () => {
      setLoading(true);
      const caregiverData = await backend.caregiver.getCaregiverForClient(caregiverRnOrShortId, clientRn);
      setCaregiverData(caregiverData);
      setLoading(false);
    });
  }, [caregiverRnOrShortId]);

  const containsMinData = React.useMemo(() => {
    const required = [
      'firstName',
      'lastName',
      'city',
      'province',
    ];
    return required.every((k) => questions?.[k] && (questions?.['personalBio'] || questions?.['caregiverMotivation']));
  }, [questions]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>
            {translate('caregiverDetail.metaData.CareQuilt')}
          </title>
        </Helmet>
        <CaregiverProfileSkeleton />
      </>
    );
  } else if (!containsMinData) {
    return (
      <>
        <Helmet>
          <title>
            {translate('caregiverDetail.metaData.NotFound')}
          </title>
        </Helmet>
        <ProfileNotAvailable />
      </>
    );
  }

  const {firstName, lastName, shortBio} = questions;
  const profilePhoto = questions.profilePhoto?.[0];

  const hasReviews = reviews.length > 0;

  const workHistory = questions.workHistory || [];
  const hasWorkHistory = workHistory.some((entry) => entry.employerName && entry.startDate);

  const hasAvailability = caregiverData?.caregiverRecommendationData?.availability?.trim();

  const careApproach = questions.personalitySummary || {};
  const expectedFields = ['descriptorChips', 'decisionStyle', 'careDrive', 'reliability', 'overallSummary'];
  const hasCareApproach = expectedFields.every((field) => Boolean(careApproach?.[field]));

  const sectionConfigs = [
    hasAvailability && {
      key: 'availability',
      title: <Translate expression="caregiverDetail.Availability.Title" />,
      content: <AvailabilitySection availabilityData={availabilityData}/>,
    },
    hasCareApproach && {
      key: 'careApproach',
      title: <Translate expression="caregiverDetail.CareApproach.Title" />,
      content: <CareApproachSection questions={questions}/>,
    },
    hasReviews && {
      key: 'reviews',
      title: <Translate expression="caregiverDetail.Review.Title" />,
      content: <ReviewSection questions={questions} reviews={reviews} />,
    },
    hasWorkHistory && {
      key: 'workHistory',
      title: <Translate expression="caregiverDetail.WorkHistory.Title" />,
      content: <WorkHistorySection questions={questions} />,
    },
    {
      key: 'credentials',
      title: <Translate expression="caregiverDetail.Credentials.Title" />,
      content: <CredentialsSection questions={questions} />,
    },
  ].filter(Boolean);

  const sectionKeys = sectionConfigs.map((section) => section.key);
  const areAllSectionsExpanded = Object.keys(expandedSectionsMap).length > 0 &&
    sectionKeys.every((key) => expandedSectionsMap[key] !== false);
  const handleToggleAllSections = () => {
    if (!sectionKeys.length) {
      return;
    }
    setExpandedSectionsMap((prev) => {
      const shouldExpand = Object.keys(prev).length < 1 || sectionKeys.some((key) => prev[key] === false);
      return sectionKeys.reduce((acc, key) => {
        acc[key] = shouldExpand;
        return acc;
      }, {});
    });
  };
  return (
    <>
      <Helmet>
        <title>
          {translate('caregiverDetail.metaData.Profile.Title', {firstName, lastName})}
        </title>
        <meta
          property="og:title"
          content={translate('caregiverDetail.metaData.Profile.Title', {firstName, lastName})}
        />
        <meta
          name="description"
          content={translate('caregiverDetail.metaData.Profile.description', {firstName, lastName, shortBio})}
        />
        <meta
          property="og:description"
          content={translate('caregiverDetail.metaData.Profile.description', {firstName, lastName, shortBio})}
        />
        {
          profilePhoto && <meta property="og:image" content={profilePhoto} />
        }
      </Helmet>
      <Stack
        spacing={{xs: 3, md: 3}}
        sx={{
          alignItems: 'center',
          maxWidth: {md: 700, lg: 1100},
          width: 'calc(100% - 16px)',
          mx: 'auto',
          mt: {xs: 1.5, sm: 2},
          mb: {xs: 25, sm: 15},
          pt: {xs: 2, sm: 3, md: 4},
          px: {xs: 3, sm: 3, md: 4},
          bgcolor: '#FFFDFC',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'color.divider',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <HeaderSection
          questions={questions}
          areAllSectionsExpanded={areAllSectionsExpanded}
          onToggleAllSections={handleToggleAllSections}
          caregiverRnOrShortId={caregiverRnOrShortId}
          clientRn={clientRn}
        />
        {
          customDescription &&
             <CustomDescriptionSection
               customDescription={customDescription}
               questions={questions}
               clientName={clientData?.clientDetail?.clientName}
             />
        }
        {
          (recommendationChips?.length || languageChips?.length) &&
          <RecommendationChipsSection
            recommendationChips={recommendationChips}
            languageChips={languageChips}
            caregiverRn={caregiverData?.caregiverRn}
          />
        }
        <Stack
          spacing={0}
          width="100%"
          sx={{
            mx: {xs: -3, md: -4},
            width: {xs: 'calc(100% + 48px)', md: 'calc(100% + 64px)'},
          }}
        >
          {
            sectionConfigs.map((section) => (
              <Accordion
                key={section.key}
                expanded={Boolean(expandedSectionsMap[section.key]) && expandedSectionsMap[section.key] !== false}
                onChange={(event, isExpanded) => {
                  setExpandedSectionsMap((prev) => ({
                    ...prev,
                    [section.key]: isExpanded,
                  }));
                }}
                sx={{
                  'width': '100%',
                  'bgcolor': '#F8F6EF',
                  'border': '1px solid',
                  'borderColor': 'color.divider',
                  'boxShadow': 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize={'large'}/>}
                  sx={{
                    'px': 3,
                    'py': 1.5,
                    'flexDirection': 'row',
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(180deg)',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{color: 'text.primary', fontWeight: 400, fontSize: '20px'}}>
                    {section.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{bgcolor: '#FFFDFC', p: {xs: 3, md: 4, lg: 6}}}>
                  {section.content}
                </AccordionDetails>
              </Accordion>
            ))
          }
        </Stack>
        {
          isStatusReview &&
          <CaregiverFeedbackSection
            clientRn={clientRn}
            caregiverRn={caregiverData.caregiverRn}
            questions={questions}
            createCaregiverFeedback={backend.clientSupport.createCaregiverFeedback}
            getCaregiverFeedback={backend.clientSupport.getCaregiverFeedback}
          />
        }
      </Stack>
    </>
  );
};

CaregiverDetail.propTypes = {
  caregiverRnOrShortId: PropTypes.string.isRequired,
  clientRn: PropTypes.string,
  clientData: PropTypes.object,
  statusProgressWrap: PropTypes.func.isRequired,
  caregiverReviews: PropTypes.object,
};

const mapStateToProps = (state) => ({
  clientRn: state.user.clientRn,
  clientData: state.clientData,
  userRn: state.user.userRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverDetail));
